/* eslint @typescript-eslint/no-explicit-any: 0 */

import type { MutableRefObject } from 'react';
import React, { useRef, useState } from 'react';

import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';
import useRegion from '~/hooks/useRegion';
import useUser from '~/hooks/useUser';
import StandardModal from '~/legacy/components/StandardModal';
import type { MainHeaderMobileProps, MenuItemProps } from '~/legacy/types';
import { bem, modalFrameSemantic } from '~/legacy/utils';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import Icon from '../Icon';
import MenuHeader from './MenuHeader';
import SubMenu from './SubMenu';

export const AngleRight = () => (
  <Icon name="angle-right" viewBox="0 0 1792 1792" fill="black" />
);

const MainHeaderMobile = ({
  menu,
  setClosed,
  setMobileMenuIsOpen,
  mobileMenuIsOpen
}: MainHeaderMobileProps) => {
  const region = useRegion();
  const [selectedItem, setSelectedItem] = useState<MenuItemProps | null>(null);
  const [idsStack, setIdsStack] = useState<Array<MenuItemProps>>([]);

  const mainNavRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const closeMenu = () => {
    setClosed();
    setMobileMenuIsOpen(!mobileMenuIsOpen);
    setSelectedItem(null);
    setIdsStack([]);
  };

  const headerBack = (currentItem: MenuItemProps) => {
    if (!idsStack.length) return;

    const newStack = idsStack.filter((item) => item.name !== currentItem.name);
    if (!newStack.length) {
      setSelectedItem(null);
      setIdsStack([]);
    } else {
      const newItem = newStack[newStack.length - 1] ?? null;
      setSelectedItem(newItem);
      setIdsStack(newStack);
    }
  };

  const selectItem = (item: MenuItemProps) => {
    setSelectedItem(item);

    idsStack.push(item);
    setIdsStack(idsStack);
    if (mainNavRef.current) {
      mainNavRef.current!.scrollTo({ block: 'start', top: 0 } as any);
    }
  };

  const env = useEnv();
  const user = useUser();

  const profileIconColor = user.isLoggedIn ? '#56BD6F' : '#A9A9A9';
  const profilePage = `${env.SITE_BASE_URL}/${
    user.isLoggedIn ? 'profile' : 'login'
  }`;

  return (
    <StandardModal
      background={modalFrameSemantic.TRANSPARENT}
      disableClickOutside
      hideClose
      isFullScreen
      isFullBody
      onCloseClick={setClosed}
      preventBodyScrollLock
    >
      <nav
        id="aria-main-navigation"
        aria-label="Main navigation"
        className="main-header-mobile"
        ref={mainNavRef}
      >
        <MenuHeader
          name={selectedItem ? selectedItem.name : ''}
          headerBack={headerBack}
          selectedItem={selectedItem}
          showBackButton={!!selectedItem}
        />
        <div className="menu-body">
          <ul
            className={bem('main-header-mobile__menu-list', {
              expanded: selectedItem,
              show: !selectedItem
            })}
          >
            {menu.map((menuItem: MenuItemProps) => (
              <React.Fragment key={menuItem.name}>
                {menuItem.type === 'filter' && (
                  <li
                    className="main-header-mobile__menu-list-item"
                    onClick={closeMenu}
                  >
                    <Tracked.ExternalLink
                      className="main-header-mobile__link main-header-mobile__link--secondary"
                      href={menuItem.link || ''}
                      pagePosition="Header"
                      name={menuItem.name as TrackedButtonClickName}
                      variant="none"
                    >
                      <div className={`main-header-mobile__item-text`}>
                        {menuItem.name}
                      </div>
                    </Tracked.ExternalLink>
                  </li>
                )}
                {(!menuItem.children || !menuItem.children.length) && (
                  <li
                    className="main-header-mobile__menu-list-item"
                    onClick={closeMenu}
                  >
                    <Tracked.ExternalLink
                      className="main-header-mobile__link"
                      href={menuItem.path}
                      pagePosition="Header"
                      name={menuItem.name as TrackedButtonClickName}
                      variant="none"
                    >
                      {menuItem.icon && (
                        <Icon
                          name={menuItem.icon}
                          width="25"
                          height="25"
                          stroke="#a9a9a9"
                          fill="#a9a9a9"
                          viewBox="0 0 28 26"
                          className="main-header-mobile__item-icon"
                        />
                      )}
                      <div
                        className={`main-header-mobile__item-text ${menuItem.name === 'Sale' ? 'w-[51px] bg-red-alert px-3 py-0.5 text-white' : ''}`}
                      >
                        {menuItem.name}
                      </div>
                    </Tracked.ExternalLink>
                  </li>
                )}

                {!!menuItem.children && !!menuItem.children.length && (
                  <>
                    <li className="main-header-mobile__menu-list-item">
                      <div
                        onClick={() => selectItem(menuItem)}
                        className={`main-header-mobile__link`}
                      >
                        {menuItem.icon && (
                          <Icon
                            name={menuItem.icon}
                            width="25"
                            height="25"
                            stroke="#a9a9a9"
                            fill="#a9a9a9"
                            viewBox="0 0 28 26"
                            className="main-header-mobile__item-icon"
                          />
                        )}
                        <div className={`main-header-mobile__item-text`}>
                          {menuItem.name}
                        </div>
                        <span
                          className={`main-header-mobile__menu-list-angle-right`}
                        >
                          {!!menuItem.children.length && <AngleRight />}
                        </span>
                      </div>
                    </li>
                    <SubMenu
                      selected={selectedItem}
                      parentName={menuItem.name}
                      closeMenu={closeMenu}
                      selectItem={selectItem}
                      menu={menuItem.children || []}
                      idsStack={idsStack}
                      path={menuItem.path || '/'}
                      nestingLevel={1}
                    />
                  </>
                )}
              </React.Fragment>
            ))}
            {region.code === 'GB' && (
              <li
                className="main-header-mobile__menu-list-item"
                onClick={closeMenu}
              >
                <a className="main-header-mobile__link" href={profilePage}>
                  <Icon
                    name="login"
                    width="25"
                    height="25"
                    viewBox="0 0 25 20"
                    fill={profileIconColor}
                    className="main-header-mobile__item-icon"
                  />
                  <div className="main-header-mobile__item-text">
                    My Profile
                  </div>
                </a>
              </li>
            )}
          </ul>
        </div>
      </nav>
    </StandardModal>
  );
};

export default MainHeaderMobile;
